import {
  Box,
  Text,
  Flex,
  Switch,
  Stat,
  StatLabel,
  Alert,
  AlertIcon,
  AlertTitle,
  useColorModeValue,
} from '@chakra-ui/react'
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts'
import React, { useState } from 'react'
import { getLocationsPercentage } from '../../../../utils/helpers'
import PercentageChange from '../../../reports/pages/components/EnergyReportComponents/PercentageChange'
import { useQueryParams } from '../../hooks/useQueryParams'
import { convertToKW, convertToKWString, PERIODPARAMS } from '../../consts'

const GROUP_LINE_COLORS = ['#EB6F21', '#21D17F', '#157FEE']

interface GroupedLineChartCardProps {
  parameters: {
    parameter_title: string
    measurement_unit: string
    value: any
    previous_value: any
    period: { bucket: string; value: number }[]
    previous_period: { bucket: string; value: number }[]
  }[]
}

export const LineChartCardGrouped: React.FC<GroupedLineChartCardProps> = ({
  parameters,
}) => {
  const [showPrevious, setShowPrevious] = useState(false)
  const bg = useColorModeValue('white', 'gray.800')
  const [queryParams] = useQueryParams(PERIODPARAMS)

  const fromDate: Date = new Date(queryParams.from)
  const toDate: Date = new Date(queryParams.to)

  const msPerDay = 1000 * 60 * 60 * 24

  const diffInDays: number = Math.round(
    (toDate.getTime() - fromDate.getTime()) / msPerDay,
  )

  const unit = parameters[0]?.measurement_unit || ''
  const title = parameters[0]?.parameter_title.replace(/[ABC]$/, '') || 'Grouped'

  const chartDataMap = new Map<string, Record<string, any>>()
  const periodType = showPrevious ? 'previous_period' : 'period'

  const hasValidPeriodData = (param: any, type: 'period' | 'previous_period') =>
    Array.isArray(param[type]) && param[type].some((pt) => typeof pt.value === 'number')

  let hasAnyData = false

  parameters.forEach((param) => {
    if (!hasValidPeriodData(param, periodType)) return

    hasAnyData = true
    const lineKey = param.parameter_title
    for (const point of param[periodType]) {
      const time =
        diffInDays >= 2
          ? new Date(point.bucket).toLocaleDateString()
          : new Date(point.bucket).toLocaleDateString(undefined, {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            })
      if (!chartDataMap.has(time)) chartDataMap.set(time, { time })
      chartDataMap.get(time)![lineKey] = convertToKW(point.value, unit)
    }
  })

  const chartData = Array.from(chartDataMap.values()).sort(
    (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime(),
  )
  const hasNonABCParameter = parameters.some((p) => !/[ABC]$/.test(p.parameter_title))

  let totalValue = 0
  let totalPrevValue = 0

  if (hasNonABCParameter) {
    const nonABCParams = parameters.filter((p) => !/[ABC]$/.test(p.parameter_title))
    totalValue = nonABCParams.reduce(
      (sum, p) => sum + (convertToKW(p.value, unit) ?? 0),
      0,
    )
    totalPrevValue = nonABCParams.reduce(
      (sum, p) => sum + (convertToKW(p.previous_value, unit) ?? 0),
      0,
    )
    if ('TotalActiveEnergy' === title) {
      const nonABCParams = parameters.filter((p) => !/[ABC]$/.test(p.parameter_title))
      totalValue = nonABCParams.reduce(
        (sum, p) =>
          sum +
          convertToKW(p.period[p.period.length - 1].value - p.period[0].value, unit),
        0,
      )
      totalPrevValue = nonABCParams.reduce(
        (sum, p) =>
          sum +
          convertToKW(
            p.previous_period[p.previous_period.length - 1].value -
              p.previous_period[0].value,
            unit,
          ),
        0,
      )
    }
  } else {
    totalValue = parameters.reduce((sum, p) => sum + (convertToKW(p.value, unit) ?? 0), 0)
    totalPrevValue = parameters.reduce(
      (sum, p) => sum + (convertToKW(p.previous_value, unit) ?? 0),
      0,
    )
    if ('TotalActiveEnergy' === title) {
      totalValue = parameters.reduce(
        (sum, p) =>
          sum +
          convertToKW(p.period[p.period.length - 1].value - p.period[0].value, unit),
        0,
      )
      totalPrevValue = parameters.reduce(
        (sum, p) =>
          sum +
          convertToKW(
            p.previous_period[p.previous_period.length - 1].value -
              p.previous_period[0].value,
            unit,
          ),
        0,
      )
    }
  }

  return (
    <Box bg={bg} p={4} borderRadius="lg" boxShadow="sm" w="100%" minH="350px">
      <Flex justify="space-between" align="center" mb={3}>
        <Stat>
          <StatLabel fontWeight={700} fontSize={14} color="#484848">
            {title}
          </StatLabel>
          <Flex
            gap={2}
            alignItems="center"
            fontWeight={700}
            fontSize="24px"
            color="#484848"
          >
            {showPrevious ? totalPrevValue.toLocaleString() : totalValue.toLocaleString()}{' '}
            <Flex gap={2}>
              <Text as="span" fontSize="15px" color="#9B9B9B" fontWeight={400}>
                {convertToKWString(unit)}
              </Text>
              <PercentageChange
                percentageChange={getLocationsPercentage(totalValue, totalPrevValue)}
              />
            </Flex>
          </Flex>
        </Stat>
        <Flex align="center" fontSize="xs" gap={2}>
          <Switch size="sm" onChange={() => setShowPrevious(!showPrevious)} />
          <Text>Show previous period</Text>
        </Flex>
      </Flex>

      <Box w="100%" h="80%">
        {hasAnyData ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.1} />
              <XAxis dataKey="time" fontSize={10} />
              <YAxis fontSize={10} />
              <Tooltip formatter={(value: number) => value.toFixed(2)} />
              {parameters.map((param, index) => {
                const isSingleLine = parameters.length === 1
                const strokeColor = GROUP_LINE_COLORS[index % GROUP_LINE_COLORS.length]

                return (
                  <Area
                    key={`${param.parameter_title}-${index}`}
                    type="monotone"
                    dataKey={param.parameter_title}
                    stroke={strokeColor}
                    fill={isSingleLine ? strokeColor : 'none'}
                    fillOpacity={isSingleLine ? 0.05 : 0}
                    strokeWidth={2}
                    dot={false}
                  />
                )
              })}
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <Flex justify="center" align="center" h="100%">
            <Alert
              status="info"
              bg="blue.50"
              borderRadius="md"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              height="100%"
              color="blue.800"
            >
              <AlertIcon boxSize="30px" mr={0} color="blue.500" />
              <AlertTitle mt={2} mb={1} fontSize="md" fontWeight="bold">
                No data available for the period
              </AlertTitle>
            </Alert>
          </Flex>
        )}
      </Box>
    </Box>
  )
}
